import { useRef, useState } from "react";
import { Plus, Trash2, UserPlus, ImageUp, KeyRound, X, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import ClientLogo from "@/components/ClientLogo";
import { CopyButton } from "@/components/MobileTools";

const EMPTY = { name: "", industry: "", brand_voice: "", login_email: "", login_password: "", logo: null };
const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
const rand = (n) => Array.from(crypto.getRandomValues(new Uint32Array(n)), (x) => CHARS[x % CHARS.length]).join("");
const genPassword = () => `${rand(5)}-${rand(4)}!${rand(3)}#${10 + (crypto.getRandomValues(new Uint32Array(1))[0] % 90)}`;
const slug = (s) => s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "").slice(0, 24);

function CredentialsDialog({ creds, onClose }) {
  const text = `Euer Zugang zum Content-Portal:\nLink: ${window.location.origin}\nBenutzername: ${creds.username}\nPasswort: ${creds.password}`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="dark text-foreground w-[95vw] max-w-md" data-testid="credentials-dialog">
        <DialogHeader><DialogTitle>Zugang für {creds.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-amber-300">Das Passwort wird nur jetzt angezeigt – bitte kopieren und sicher speichern.</p>
        <pre className="rounded-lg bg-white/[0.04] border border-border p-4 text-sm whitespace-pre-wrap font-mono select-all" data-testid="credentials-text">{text}</pre>
        <div className="grid grid-cols-2 gap-2">
          <CopyButton text={creds.password} label="Passwort" testId="copy-password-button" />
          <CopyButton text={text} label="Alles für WhatsApp" testId="copy-credentials-button" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { clients, reload, setClientId } = useClient();
  const [form, setForm] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const [creds, setCreds] = useState(null);
  const bgRef = useRef(null);

  const uploadLogo = async (cid, file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/clients/${cid}/logo`, fd);
  };
  const create = async () => {
    try {
      const body = { name: form.name, industry: form.industry, brand_voice: form.brand_voice, login_email: form.login_email.trim() || null, login_password: form.login_email.trim() ? form.login_password : null };
      const r = await api.post("/clients", body);
      if (form.logo) await uploadLogo(r.data.id, form.logo).catch(() => toast.error("Logo konnte nicht gespeichert werden"));
      await reload();
      setClientId(r.data.id);
      setForm(null);
      toast.success("Kunde angelegt");
      if (form.login_email.trim()) setCreds({ name: form.name, username: form.login_email.trim().toLowerCase(), password: form.login_password });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addLogin = async () => {
    try {
      await api.post(`/clients/${loginFor.id}/logins`, loginFor.form); await reload();
      setCreds({ name: loginFor.form.name, username: loginFor.form.email.trim().toLowerCase(), password: loginFor.form.password });
      setLoginFor(null); toast.success("Zugang erstellt");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPw = async (c, l) => {
    if (!window.confirm(`Neues Passwort für „${l.email}“ erstellen? Das alte funktioniert dann nicht mehr.`)) return;
    const pw = genPassword();
    try { await api.post(`/clients/${c.id}/logins/${l.id}/password`, { password: pw }); setCreds({ name: c.name, username: l.email, password: pw }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delLogin = async (c, l) => { if (window.confirm(`Zugang „${l.email}“ löschen?`)) { await api.delete(`/clients/${c.id}/logins/${l.id}`); reload(); } };
  const remove = async (c) => { if (window.confirm(`${c.name} wirklich löschen?`)) { await api.delete(`/clients/${c.id}`); const list = await reload(); if (list[0]) setClientId(list[0].id); } };

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Verwaltung</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Kunden</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => bgRef.current?.click()} className="gap-2" data-testid="login-bg-upload-button"><ImageUp className="h-4 w-4" />Login-Hintergrund</Button>
          <input ref={bgRef} type="file" accept="image/*" className="hidden" data-testid="login-bg-input" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const fd = new FormData(); fd.append("file", f);
            try { await api.post("/settings/login-bg", fd); toast.success("Login-Hintergrund aktualisiert"); } catch (err) { toast.error(errMsg(err)); }
            e.target.value = "";
          }} />
          <Button onClick={() => setForm({ ...EMPTY, login_password: genPassword() })} className="bg-teal-500 hover:bg-teal-400 text-slate-950 gap-2" data-testid="new-client-button"><Plus className="h-4 w-4" />Neuer Kunde</Button>
        </div>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border" data-testid="clients-list">
        {clients.map((c) => (
          <div key={c.id} className="flex flex-wrap items-start gap-3 px-5 py-4" data-testid={`client-row-${c.id}`}>
            <div className="flex-1 min-w-[200px]">
              {c.has_logo && <div className="mb-1"><ClientLogo client={c} className="h-5" testId={`client-row-logo-${c.id}`} /></div>}
              <p className="font-medium">{c.name}</p>
              <p className="text-xs text-muted-foreground mb-2">{c.industry || "—"}</p>
              <div className="flex flex-wrap gap-2" data-testid={`client-logins-${c.id}`}>
                {(c.logins || []).length === 0 && <span className="text-xs text-slate-500">kein Login</span>}
                {(c.logins || []).map((l) => (
                  <span key={l.id} className="inline-flex items-center gap-2 text-xs rounded-full border border-border pl-3 pr-1 py-1">
                    <KeyRound className="h-3 w-3 text-teal-400" />{l.email}
                    <button onClick={() => resetPw(c, l)} className="px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10" data-testid={`reset-password-${l.id}`}>Neues Passwort</button>
                    <button onClick={() => delLogin(c, l)} className="text-slate-500 hover:text-rose-400 px-1" data-testid={`delete-login-${l.id}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-white/5" data-testid={`upload-logo-${c.id}`}>
              <ImageUp className="h-3.5 w-3.5" />{c.has_logo ? "Logo ändern" : "Logo"}
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData(); fd.append("file", f);
                try { await api.post(`/clients/${c.id}/logo`, fd); await reload(); toast.success("Logo gespeichert"); } catch (err) { toast.error(errMsg(err)); }
                e.target.value = "";
              }} />
            </label>
            <Button variant="outline" size="sm" onClick={() => setLoginFor({ id: c.id, form: { name: c.name, email: slug(c.name), password: genPassword() } })} className="gap-1.5" data-testid={`add-login-${c.id}`}><UserPlus className="h-3.5 w-3.5" />Zugang</Button>
            <button onClick={() => remove(c)} className="text-slate-500 hover:text-rose-400" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="dark text-foreground w-[95vw] max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Neuer Kunde</DialogTitle></DialogHeader>
            {[["name", "Firmenname"], ["industry", "Branche"], ["brand_voice", "Markenstimme / Tonalität"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label>{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value, ...(k === "name" && (!form.login_email || form.login_email === slug(form.name)) ? { login_email: slug(e.target.value) } : {}) })} data-testid={`client-${k}-input`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Logo</Label>
              <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-white/5" data-testid="client-logo-input-label">
                <ImageUp className="h-4 w-4" /><span className="truncate">{form.logo ? form.logo.name : "Logo auswählen (optional)"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setForm({ ...form, logo: e.target.files?.[0] || null })} data-testid="client-logo-input" />
              </label>
            </div>
            <div className="rounded-lg border border-teal-500/30 bg-teal-500/[0.05] p-3 space-y-3">
              <p className="text-sm font-medium flex items-center gap-2"><KeyRound className="h-4 w-4 text-teal-400" />Zugang für den Kunden</p>
              <div className="space-y-1.5"><Label>Benutzername</Label><Input value={form.login_email} autoCapitalize="none" onChange={(e) => setForm({ ...form, login_email: e.target.value })} data-testid="client-login_email-input" /></div>
              <div className="space-y-1.5"><Label>Passwort</Label>
                <div className="flex gap-2">
                  <Input value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className="font-mono" data-testid="client-login_password-input" />
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, login_password: genPassword() })} className="gap-1.5 shrink-0" data-testid="generate-password-button"><Wand2 className="h-4 w-4" />Erzeugen</Button>
                </div>
              </div>
            </div>
            <Button onClick={create} disabled={!form.name} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="create-client-submit">Kunde anlegen</Button>
          </DialogContent>
        </Dialog>
      )}
      {loginFor && (
        <Dialog open onOpenChange={() => setLoginFor(null)}>
          <DialogContent className="dark text-foreground">
            <DialogHeader><DialogTitle>Kundenzugang erstellen</DialogTitle></DialogHeader>
            {[["name", "Anzeigename"], ["email", "Benutzername oder E-Mail"], ["password", "Passwort"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label>{l}</Label>
                <Input value={loginFor.form[k]} autoCapitalize="none" className={k === "password" ? "font-mono" : ""} onChange={(e) => setLoginFor({ ...loginFor, form: { ...loginFor.form, [k]: e.target.value } })} data-testid={`login-${k}-input`} />
              </div>
            ))}
            <Button type="button" variant="outline" onClick={() => setLoginFor({ ...loginFor, form: { ...loginFor.form, password: genPassword() } })} className="gap-1.5" data-testid="login-generate-password"><Wand2 className="h-4 w-4" />Neues Passwort erzeugen</Button>
            <Button onClick={addLogin} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="create-login-submit">Erstellen</Button>
          </DialogContent>
        </Dialog>
      )}
      {creds && <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}
